import React, { useState, useEffect, useRef, useContext } from 'react';
import Dropdown, { DropdownButton, DropdownMenu } from '../dropdown/dropdown';

export { default as Option } from './selectOption';
const SelectContext = React.createContext({});
export interface ISelectProps {
  id?: string;
  /**
   * Define if select is large
   */
  large?: boolean;
  /**
   * Define if is select filter
   */
  filter?: boolean;
  /**
   * Define if is select multiple
   */
  multiple?: boolean;
  /**
   * Define if select is disabled
   */
  disabled?: boolean;
  /**
   * Define if select is required
   */
  required?: boolean;
  /**
   * Define if select have an error
   */
  error?: boolean;

  /**
   * Set label for select
   */
  label?: string;
  /**
   * Set helper text for select
   */
  helperText?: string;
  /**
   * Set the icon in helper text. It's a name of material icon, example: "info"
   */
  helperIcon?: string;
  /**
   * Set placeholder for select
   */
  placeholder: string;
  /**
   * Sets the display text in the input field when items are selected. Note that this should reflect both singular and plural cases, especially when the 'multiple' prop is passed. 
   */
  optionsPlaceholder?: string;
  /**
   * Set different classes for select component
   */
  className?: string;

  /**
   * Set the name of value in a form
   */
  name: string;
  /**
   * Set the value of select
   */
  value: any;
  /**
   * Return the value of select
   */
  onChange: (value: any) => void;
  /**
   * Sets the value of the filter,
   * in case you manage the options representation outside the component,
   * you will need to manage this state outside this component as well,
   * in case you are using the filter option
   */
  filterValue?: string;
  /**
   * Return the value of filter value (remember the previous explanation)
   */
  onChangeFilter?: (filterValue: string) => void;
    /**
   * This allows for multiple chips, one chip per selection. Multiple prop has to be set to true for multipleChips to work.
   */
  multipleChips?: boolean;

  /**
   For single selection (multiple prop is not enabled) when the user clicks on an already selected item, it will not remove the item but instead keep it. The click will not trigger any events.
   For example, if user clicks on 'Red' then 'Red' will be kept. Normal behaviour would be (without keepSelectedValue): user clicks on 'Red' and then the item 'Red' is deselected.
  */
  keepSelectedValue?: boolean;
  /**
   * Sets the display value count in the input field when items are selected. Note that this should reflect both singular and plural cases, especially when the 'multiple' prop is passed. 
   */
  optionsPlaceholderValue?: boolean;

  [others: string]: any;
}

interface ISelectContext {
  multiple: boolean;
  selectedValue: any;
  filterValue: string;
  onSelectItem: (selectValue: string | number) => void;
  onRemoveItem: (removeValue: string | number) => void;
}

const Select: React.FC<ISelectProps> = ({
  id,
  large,
  required,
  filter,
  multiple,
  optionsPlaceholderValue = false,
  disabled,
  error,

  label,
  placeholder,
  optionsPlaceholder = multiple ? 'Options selected' : 'Option selected',
  helperText,
  helperIcon,

  name,
  className,
  value,

  onChange,

  filterValue,
  onChangeFilter,
  children,
  multipleChips = false,
  keepSelectedValue = false,
  ...rest
}) => {
  const inputRef: any = useRef<HTMLInputElement>();
  const [filterVal, setFilterVal] = useState<string>(filterValue || '');
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    if (!filterValue) return;
    
    if (filter && typeof onChangeFilter === 'function') {
      if (filterValue !== filterVal) {
        setFilterVal(filterValue);
      }
    }
  }, [filterValue]);


  const onSelectItem = (selectValue: string) => {
    if (!multiple) {
      onChange(selectValue);
    } else {
      let newValues = Array.isArray(value) ? value : [];
      if (!newValues.some((existingValue: string) => existingValue === selectValue)) {
        newValues = [...newValues, selectValue];

        onChange(newValues);
      }
    }

    if (!multiple && filter) {
      setFilterVal('');
      if (typeof onChangeFilter === 'function') {
        onChangeFilter('');
      }
    }
  };

  const onRemoveItem = (deleteValue: string) => {
    let newValues: any;
    if (!multiple) {
      if (deleteValue === value && keepSelectedValue) {
        return;
      }
      newValues = '';
      if (onChange) onChange('');
    } else {
      newValues = value.filter((existingValue: string) => existingValue !== deleteValue);
      onChange(newValues);
    }
  };

  // This executes when the user clicks to remove a chip
  const onRemoveOptions = () => {
    if (multiple) {
      if (onChange) onChange([]);
    } else {
      if (onChange) onChange('');
      setFilterVal('');
    }
    if (typeof onChangeFilter === 'function') {
      onChangeFilter('');
    }
  };

  // when multipleChips is true this code executes
  const onRemoveChip = (item: string) => {
    const newValues = value.filter((existingValue: string) => existingValue !== item);
    onChange(newValues);
  };

  // this sets the text in the input (Not multiple)
  const filterItems = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    // setShowMenu(!!val);
    // if (!val) {
    //   onChange('');
    // }
    if (typeof onChangeFilter === 'function') {
      onChangeFilter(val);
    }
    setFilterVal(val);
  };

  // This executes when the user clicks on the 'x' to remove the input value
  const onRemoveFilterValue = () => {
    if (typeof onChangeFilter === 'function') {
      onChangeFilter('');
      onChange('');
    }
    setFilterVal('');
  };

  function selectPlaceholder() {
    if (value && value?.length) {
      if (multiple) {
        return `${optionsPlaceholderValue ? `${optionsPlaceholder}` : `${value.length} ${optionsPlaceholder}`}`;
      }
      return `${optionsPlaceholderValue ? `${optionsPlaceholder}` : `1 ${optionsPlaceholder}`}`;
    }
    // Fallback to the default placeholder if no value is selected
    return placeholder;
  }

  const renderSelectContainer = () => {
    if (filter) {
      return (
        <div className="input-container">
          <input
            data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-input` : undefined}
            className={`input ${large ? 'large' : ''}`}
            placeholder={selectPlaceholder()}
            ref={inputRef}
            type="text"
            value={filterVal || ''}
            onChange={filterItems}
            disabled={disabled}
          />
          {filterVal ? (
            <span
              data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-remove-filter-btn` : undefined}
              className="material-icons input-icon-box"
              onClick={onRemoveFilterValue}
            >
              close
            </span>
          ) : (
            <span className="material-icons input-icon-box">search</span>
          )}
        </div>
      );
    }

    return (
      <div className="input-container">
        <div className={large ? 'input_large' : 'input'}>{getTextContent()}</div>
        {showMenu ? (
          <span className="material-icons input-icon-box">expand_less</span>
        ) : (
          <span className="material-icons input-icon-box">expand_more</span>
        )}
      </div>
    );
  };

  const getTextContent = () => {
    if (multiple && typeof value === 'object' &&  value?.length) {
      return (
        <span className="placeholder">
          {!optionsPlaceholder ? `${value.length} Options selected` :
           optionsPlaceholderValue ? `${optionsPlaceholder}` : `${value.length} ${optionsPlaceholder}` }
        </span>
      );
    } else if (value && children && !multiple) {
      const itemSelected = React.Children.toArray(children).find((item: any) => item.props?.value === value);
      if (itemSelected) return (itemSelected as any).props?.label;
    }
    return <span className="placeholder">{placeholder}</span>;
  };

  // Chip
  const renderBottomContainer = () => {
    if (!value?.length) return;
    // First, check if multipleChips is true and render its content exclusively
    if (multipleChips && multiple) {
      const items = React.Children.toArray(children).map((item: any) => item.props);
      const getLabel = (itemValue: string | number) => items.find((item) => item.value === itemValue)?.label;

      const chipsContent = value.map((item: string, key: number) => {
        const label = getLabel(item);
        if (label) {
          return (
            <button
              key={`${item}-${key}`}
              type="button"
              className={`chip_active${disabled ? '_disabled' : ''} m0`}
              onClick={() => onRemoveChip(item)}
            >
              <span style={{ maxWidth: '260px', overflowX: 'hidden' }}>{label}</span>
              <span className="material-icons right">close</span>
            </button>
          );
        }
        return null;
      });
  
      return (
        <div className="select-multiple-chips-container">
          {chipsContent}
          {helperText && (
            <p className="input-helper-text">
              {helperIcon && <span className="material-icons">{helperIcon}</span>}
              {helperText}
            </p>
          )}
        </div>
      );
    }
    return null;
  };
  
  return (
    <SelectContext.Provider value={{multiple, selectedValue: value, filterValue: filterVal, onSelectItem, onRemoveItem}}>
      <div className={`input-wrapper${disabled ? '_disabled' : ''} ${error ? 'error' : ''}  ${className || ''}`}>
        {label && (
          <label className="caption">
            {required && <small>*</small>}
            {label}
          </label>
        )}
        <div className="select-wrapper" data-filter={filter}>
          <Dropdown
            id={`${'filter'}-dropdown-${id ? id : ''}`}
            onChangeToggleMenu={(state: boolean) => setShowMenu(state)}
            keepShown={multiple}
            filterValue={filterVal}
          >
            <DropdownButton data-testid={rest && rest['data-testid'] ? rest['data-testid'] : undefined} disabled={disabled}>
              {renderSelectContainer()}
            </DropdownButton>
            <DropdownMenu>
              {children}
            </DropdownMenu>
          </Dropdown>
          <input type="hidden" readOnly name={name} disabled={disabled} value={value} />
        </div>

        {renderBottomContainer()}
      </div>
    </SelectContext.Provider>
  );
};

export default Select;
/**
 *
 * @internal
 */
export const useSelect = () => useContext(SelectContext) as ISelectContext;
